import React from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";

/**
 * Redirect a legacy `/admin/tenants/*` URL to its `/tenant-management/*` replacement,
 * carrying the whole location across — not just the path.
 *
 * The tenant-management module moved off `/admin/tenants/*` on 2026-08-07 so that the
 * backend module id, the frontend folder name and the URL namespace all read the same
 * (vault/how-to/how-to-build-new-module.md § Key File Locations). The old paths are kept
 * alive indefinitely rather than cut: they are in bookmarks, in runbooks, and in
 * cross-module deep links.
 *
 * Why this exists instead of a bare `<Navigate to="…" />`:
 *   1. react-router does NOT interpolate params in `to`, so `/tenant-management/:id/edit`
 *      would navigate to that string literally, param placeholder and all.
 *   2. `<Navigate>` uses only the `to` it is given — the query string and the `#hash` of
 *      the current location are dropped. The tenant edit page selects its section from the
 *      hash (`#hosting`, `#email`, `#billing`), so dropping it would land every deep link
 *      on the default section and look like the link was stale.
 *
 * An unmatched `:param` is left verbatim rather than replaced with an empty string — a
 * visibly wrong URL is easier to diagnose than a silently truncated one.
 */
export const LegacyTenantRedirect: React.FC<{ to: string }> = ({ to }) => {
    const params = useParams();
    const location = useLocation();
    const path = to.replace(/:([A-Za-z0-9_]+)/g, (match, key) => params[key] ?? match);
    return <Navigate to={`${path}${location.search}${location.hash}`} replace />;
};

export default LegacyTenantRedirect;
